import React, { useEffect, useState, useMemo, useRef, useCallback } from "react";
import { Row, Col, Card, Badge } from "react-bootstrap";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  Filler
} from 'chart.js';

import { Line } from "react-chartjs-2";
import ChartDataLabels from 'chartjs-plugin-datalabels';
import { WiHumidity, WiThermometer } from "react-icons/wi";
import { TbPackage } from "react-icons/tb";
import { HiOutlineBell, HiOutlineRefresh, HiOutlineExclamation } from "react-icons/hi";

import Api from "../../../../services/Api";
import { formatLocal } from '../../../../utilis/dateUtils';

// Registrar os componentes do Chart.js
ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  Filler,
  ChartDataLabels
);

const plugins = {
  afterDraw: function (chart) {
    if (chart.data.datasets.length > 0) {
      if (chart.data.datasets[0].data.length === 0) {
        var width = chart.width,
          height = chart.height,
          ctx = chart.ctx;
        ctx.restore();
        var fontSize = (height / 160).toFixed(2);
        ctx.font = fontSize + "em sans-serif";
        ctx.textBaseline = "top";
        var text = "Nenhuma Informação Disponível",
          textX = Math.round((width - ctx.measureText(text).width) / 2),
          textY = height / 2;
        ctx.fillText(text, textX, textY);
        ctx.save();
      }
    }
  },
}

const options = {
  responsive: true,
  maintainAspectRatio: false,
  scales: {
    y: {
      beginAtZero: false,
      ticks: {
        precision: 1,
        color: '#b0b0b0',
        font: {
          size: 11
        }
      },
      grid: {
        color: 'rgba(255, 255, 255, 0.1)',
        borderDash: [5, 5]
      }
    },
    x: {
      ticks: {
        maxRotation: 45,
        minRotation: 45,
        color: '#b0b0b0',
        font: {
          size: 10
        }
      },
      grid: {
        display: false
      }
    },
  },
  animations: {
    radius: {
      duration: 500,
      easing: 'linear',
      loop: (context) => context.active
    }
  },
  animation: {
    duration: 400,
  },
  hoverRadius: 8,
  hoverBackgroundColor: '#a4c639',
  interaction: {
    mode: 'nearest',
    intersect: false,
    axis: 'x'
  },
  plugins: {
    legend: {
      display: false
    },
    tooltip: {
      enabled: true,
      backgroundColor: 'rgba(40, 44, 52, 0.9)',
      titleColor: '#e0e0e0',
      bodyColor: '#e0e0e0',
      titleFont: {
        size: 12
      },
      bodyFont: {
        size: 11
      },
      padding: 10,
      cornerRadius: 8,
      displayColors: false,
      borderColor: 'rgba(164, 198, 57, 0.3)',
      borderWidth: 1
    },
    title: {
      display: true,
      text: '',
      font: {
        size: 14,
        weight: 'bold'
      },
      color: '#e0e0e0'
    },
    datalabels: {
      color: '#e0e0e0',
      font: {
        weight: 'bold',
        size: 12
      },
      // default: hide all labels, will enable only for last point via dataset config
      formatter: (value, context) => {
        return '';
      },
      align: 'center',
      offset: 0
    }
  }
};


const MAX_MEDIDAS = 50;

const toTimestamp = (rawDate) => {
  if (!rawDate) return NaN;
  const str = String(rawDate).trim();
  // Remove o "Z" e qualquer timezone (vai usar hora local do navegador)
  const cleanStr = str.replace(/([zZ]|[+-]\d{2}:?\d{2})$/, '');
  const ms = Date.parse(cleanStr);
  return Number.isNaN(ms) ? NaN : ms;
};

const isWithin75Minutes = (lastDateString) => {
  const lastMs = toTimestamp(lastDateString);
  if (Number.isNaN(lastMs)) return false;
  const nowMs = Date.now();
  if (lastMs > nowMs) return true; // data futura = erro de relógio, considera recente
  return (nowMs - lastMs) / 60000 <= 75;
};

const Grafico = ({ serial, company, reload, detailsEquip, progressReset, alarmInfo = null, rotationInfo = null }) => {
  const { configs, equipments, _id } = company;

  // Refs para gradiente — evita variáveis globais compartilhadas entre instâncias
  const gradientRef = useRef(null);
  const gradientWidthRef = useRef(null);
  const gradientHeightRef = useRef(null);

  const getGradient = useCallback((ctx, chartArea) => {
    const chartWidth = chartArea.right - chartArea.left;
    const chartHeight = chartArea.bottom - chartArea.top;
    if (!gradientRef.current || gradientWidthRef.current !== chartWidth || gradientHeightRef.current !== chartHeight) {
      gradientWidthRef.current = chartWidth;
      gradientHeightRef.current = chartHeight;
      gradientRef.current = ctx.createLinearGradient(0, chartArea.bottom, 0, chartArea.top);
      gradientRef.current.addColorStop(1, "#a4c639");
      gradientRef.current.addColorStop(0.7, "#3c834d");
      gradientRef.current.addColorStop(0.3, "#2a5e38");
      gradientRef.current.addColorStop(0, "#ff7e5f");
    }
    return gradientRef.current;
  }, []);

  const equipamento = useMemo(() => {
    return detailsEquip.find((equip) => equip?.serialnumber === serial);
  }, [serial, detailsEquip]);

  const colSize = useMemo(() => {
    if (equipments.length === 1) return 12;
    if (equipments.length <= 4) return 6;
    return 4;
  }, [equipments.length]);

  const [medidas, setMedidas] = useState([]);
  const [status, setStatus] = useState(null);
  const [ultimaMedida, setUltimaMedida] = useState({
    _id: "",
    serialnumber: "",
    date: "",
    idcalib: "",
    grain: "",
    humidity: "",
    temperature: "",
    peso: "",
    freqc: "",
    freqv: "",
    ph: "",
    curvavalidade: "",
    companyId: "",
    localEquipament: ""
  });
  const [fardosPorHora, setFardosPorHora] = useState(null);  // Estado para armazenar fardos por hora (null = ainda não carregado)

  const handleEquipment = useCallback(async () => {
    try {
      const response = await Api.get(`/api/v1/measurements/${serial}/${_id}`);
      
      if (response.data?.status?.includes("Não há medidas")) {
        setMedidas([]);
        return;
      }

      if (Array.isArray(response.data) && response.data.length > 0) {
        const formattedData = response.data.map(item => ({
          _id: item.id || "",
          serialnumber: item.serialnumber || serial,
          date: item.date || "",
          humidity: parseFloat(item.humidity || item.umidade || "0"),
          temperature: parseFloat(item.temperature || item.temp || item.temperatura || "0"),
          grain: item.grain || "",
          freqc: item.freqc || "",
          freqv: item.freqv || item.freq_v || item.freq || "",
          ph: item.ph || "0"
        }));

        const orderedData = formattedData
          .sort((a, b) => toTimestamp(a.date) - toTimestamp(b.date))
          .slice(-MAX_MEDIDAS); // limita para evitar acúmulo de memória em uso contínuo

        setMedidas(orderedData);
        if (orderedData.length > 0) {
          setUltimaMedida(orderedData[orderedData.length - 1]);
        }
      }
    } catch (error) {
      console.error(`[Grafico:${serial}] Erro ao buscar medidas:`, error?.response?.data || error?.message);
    }
  }, [serial, _id]);

  const handleGetStatus = useCallback(async () => {
    try {
      // Consultando status diretamente do sistema local
      const response = await Api.get(`/connection/status/serial/${serial}`);
      setStatus(response.data?.status);
    } catch (error) {
      console.error('Erro ao verificar status do dispositivo:', error?.message);
      setStatus("offline");
    }
  }, [serial]);

  useEffect(() => {
    // buscar imediatamente quando reload ou serial mudarem
    handleEquipment();
    handleGetStatus();

  }, [reload, serial, handleEquipment, handleGetStatus]);

  // polling controlado pelo ProgressBar: quando progressReset muda, reinicia um polling de 30s
  const progressResetFirstRun = useRef(true);
  useEffect(() => {
    // Pula a primeira execução (mount). Só inicia polling quando progressReset é alterado depois do mount.
    if (progressResetFirstRun.current) {
      progressResetFirstRun.current = false;
      return;
    }

    let pollingInterval;

    // iniciar imediatamente uma chamada para garantir dados frescos quando a barra reiniciar
    handleEquipment();

    pollingInterval = setInterval(() => {
      handleEquipment();
      handleGetStatus();
    }, 30000);

    return () => {
      if (pollingInterval) clearInterval(pollingInterval);
    };
  }, [progressReset, handleEquipment, handleGetStatus, serial]);

  // Tick a cada 30s para re-avaliar recência sem depender de novo polling
  const [, setTick] = useState(0);
  useEffect(() => {
    const id = setInterval(() => setTick(t => t + 1), 30000);
    return () => clearInterval(id);
  }, []);

  const isFardDevice = serial.includes('FARD');
  const showFardColumn = isFardDevice && fardosPorHora !== null && fardosPorHora !== undefined;
  const metricColSize = showFardColumn ? 4 : 6;

  const isRecent = isWithin75Minutes(ultimaMedida.date);

  // Função simples para verificar se a medida é recente (≤5 minutos)
  // Compara diretamente os timestamps sem timezone (ambos em hora local)
  const isMeasurementRecent5min = useCallback(() => {
    if (!ultimaMedida?.date) {
      return false;
    }
  
    const measureMs = toTimestamp(ultimaMedida.date);
    const nowMs = Date.now();
    
    if (Number.isNaN(measureMs)) {
      return false;
    }
    
    const diffMs = nowMs - measureMs;
    const limit5min = 5 * 60 * 1000;
    
    const isRecent = diffMs >= 0 && diffMs <= limit5min;
    
    return isRecent;
  }, [ultimaMedida?.date, serial]);

  const outOfRange = useMemo(() => {
    // Retorna false se não há alarmInfo, alarm está inativo, ou sem medida de umidade
    if (!alarmInfo || !alarmInfo.active || !ultimaMedida?.humidity) {
      return false;
    }
    
    // Verifica recência
    const isRecent = isMeasurementRecent5min();
    
    // Verifica se a umidade está fora do range definido
    const humidity = Number(ultimaMedida.humidity);
    const min = alarmInfo?.minHumidity !== '' && alarmInfo?.minHumidity != null ? Number(alarmInfo.minHumidity) : null;
    const max = alarmInfo?.maxHumidity !== '' && alarmInfo?.maxHumidity != null ? Number(alarmInfo.maxHumidity) : null;
    
    const isOutOfRange = (min !== null && humidity < min) || (max !== null && humidity > max);
    
    // Mostra cor laranja APENAS se está fora do range E a medida é recente
    return isRecent && isOutOfRange;
  }, [ultimaMedida.humidity, ultimaMedida.date, alarmInfo, isMeasurementRecent5min, serial]);

  // calcular opções do chart dinamicamente para ajustar escala Y
  const chartOptions = useMemo(() => {
    // copiar opções base
    const opts = JSON.parse(JSON.stringify(options));

    // extrair valores de umidade atuais
    const values = medidas.map(m => (typeof m.humidity === 'number' ? m.humidity : Number(m.humidity || 0)));
    let dataMax = values.length ? Math.max(...values) : (configs?.maxHumidity ?? 0);
    let dataMin = values.length ? Math.min(...values) : (configs?.minHumidity ?? 0);

    // se não houver variação significativa, garantir um range visível
    const span = Math.max(0, dataMax - dataMin);

    let yMax, yMin;
    if (dataMax <= 15) {
      // exemplo pedido: para valores pequenos, mostrar 0..15
      yMax = 15;
      yMin = 0;
    } else {
      // aplicar padding proporcional (mínimo 1)
      const pad = Math.max(1, span * 0.5);
      yMax = Math.ceil(dataMax + pad);
      yMin = Math.floor(Math.max(0, dataMin - pad));
    }

    // garantir que yMin seja no máximo yMax-1
    if (yMin >= yMax) {
      yMin = Math.max(0, Math.floor(yMax - 1));
    }

    opts.scales = opts.scales || {};
    opts.scales.y = opts.scales.y || {};
    opts.scales.y.min = yMin;
    opts.scales.y.max = yMax;

    return opts;
  }, [medidas, configs]);

  const chartData = useMemo(() => ({
    labels: medidas.map(data =>
      formatLocal(data.date, undefined, {
        day: '2-digit',
        month: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
        timeZone: 'UTC'
      })
    ),
    datasets: [{
      label: 'UMIDADE',
      backgroundColor: "rgba(60, 131, 77, 0.1)",
      data: medidas.map(data => Number(parseFloat(data?.humidity || 0).toFixed(1))),
      borderColor: (context) => {
        const { ctx, chartArea } = context.chart;
        if (!chartArea) return;
        return getGradient(ctx, chartArea);
      },
      borderWidth: 3,
      fill: true,
      pointRadius: (context) => {
        const v = context.dataset.data[context.dataIndex];
        return v !== null && v !== undefined ? 3 : 0;
      },
      pointBackgroundColor: "#a4c639",
      pointBorderColor: "#1e2128",
      pointBorderWidth: 1.5,
      pointHoverRadius: 6,
      pointHoverBackgroundColor: "#a4c639",
      pointHoverBorderColor: "#1e2128",
      pointHoverBorderWidth: 2,
      tension: 0.3,
      datalabels: {
        display: 'auto',
        formatter: (value) => (value !== undefined && value !== null ? value : ''),
        color: '#e0e0e0',
        backgroundColor: 'rgba(40, 44, 52, 0.65)',
        borderRadius: 4,
        padding: 4,
        font: { size: 10, weight: 'bold' },
        anchor: 'end',
        align: 'top',
        offset: -6
      },
    }],
  }), [medidas, getGradient]);

  return (
    <Col xs={12} sm={12} md={12} lg={12} xl={colSize} xxl={colSize} className="mb-1">
      <Card className={`h-100 shadow dark-card${outOfRange ? ' grafico-alert' : ''}`} style={{ borderRadius: '16px', overflow: 'hidden', border: outOfRange ? '2px solid #fd7e14' : 'none', backgroundColor: '#282c34' }}>
        <Card.Body className="p-2">
          <Row className="g-3 mb-2">
            <Col md={4} className="text-center">
              <h6 className="mb-1 dark-text-secondary" style={{ fontSize: '0.8rem' }}>EQUIPAMENTO</h6>
              {status === "online" ? (
                <Badge bg="success" pill className="mb-2 px-3 py-2">ONLINE</Badge>
              ) : status === "unstable" ? (
                <Badge bg="warning" text="dark" pill className="mb-2 px-3 py-2">INSTÁVEL</Badge>
              ) : (
                <Badge bg="danger" pill className="mb-2 px-3 py-2">OFFLINE</Badge>
              )}

              <h5 className="mb-1 dark-text-accent" style={{ fontWeight: 600 }}>
                {serial || "--"}
              </h5>

              {/* Ícones de alarme e rodízio */}
              <div className="d-flex justify-content-center align-items-center gap-2 mt-1">
                {alarmInfo && (
                  <span title={`Alarme ${alarmInfo.active ? 'ativo' : 'inativo'}${alarmInfo.description ? `: ${alarmInfo.description}` : `: ${alarmInfo.alarmSerial}`}`}>
                    <HiOutlineBell
                      size={18}
                      style={{ color: outOfRange ? '#fd7e14' : 'rgba(255,255,255,0.35)' }}
                    />
                  </span>
                )}
                {rotationInfo && (
                  <span title={`Rodízio ${rotationInfo.rotationOn ? 'ativo' : 'inativo'}${rotationInfo.description ? `: ${rotationInfo.description}` : `: ${rotationInfo.rotationSerial}`}`}>
                    <HiOutlineRefresh
                      size={18}
                      className={rotationInfo.rotationOn ? 'grafico-icon-spinning' : ''}
                      style={{ color: rotationInfo.rotationOn ? '#7CB342' : 'rgba(255,255,255,0.35)' }}
                    />
                  </span>
                )}
              </div>

              {outOfRange && (
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '4px', marginTop: '6px', color: '#fd7e14', fontSize: '0.72rem', fontWeight: 600 }}>
                  <HiOutlineExclamation size={13} />
                  Umidade fora do limite
                </div>
              )}
            </Col>

            {ultimaMedida?.grain ?
              <Col md={4} className="text-center">
                <h6 className="mb-1 dark-text-secondary" style={{ fontSize: '0.8rem' }}>GRÃO</h6>
                <h5 className="mb-0 dark-text" style={{ fontWeight: 600 }}>
                  {ultimaMedida?.grain?.toUpperCase() || "--"}
                </h5>
              </Col>
              :
              <Col md={4} className="text-center">
              </Col>
            }

            <Col md={4} className="text-center">
              <h6 className="mb-1 dark-text-secondary" style={{ fontSize: '0.8rem' }}>ÚLTIMA MEDIDA</h6>
              <h5 className="mb-0" style={{ color: isRecent ? "#a4c639" : "#ff5252", fontWeight: 600 }}>
                {ultimaMedida?.date
                  ? formatLocal(ultimaMedida.date, undefined, {
                    day: '2-digit',
                    month: '2-digit',
                    year: '2-digit',
                    hour: '2-digit',
                    minute: '2-digit',
                    timeZone: 'UTC'
                  })
                  : "--"}
              </h5>
              <h6 className="mb-0 dark-text" style={{ fontWeight: 500 }}>
                {equipamento?.nameEquipament?.toUpperCase() || "--"}
              </h6>
            </Col>
          </Row>

          <Row className="mb-2 g-2">
            <Col md={metricColSize}>
              <Card className="mb-0 h-100 shadow-sm dark-card" style={{ borderRadius: '12px', border: 'none', backgroundColor: '#1e2128' }}>
                <Card.Body className="p-2 text-center">
                  <div className="d-flex align-items-center justify-content-center mb-2">
                    <WiHumidity size={30} color="#a4c639" />
                    <h6 style={{ color: "#a4c639", marginBottom: 0, marginLeft: '5px' }}>UMIDADE</h6>
                  </div>
                  <h1 style={{ color: "#e0e0e0", marginBottom: 0, fontWeight: 700 }}>
                    <span style={{ fontSize: "2.0rem" }}>
                      {ultimaMedida?.humidity ? Math.floor(ultimaMedida.humidity) : "--"}
                    </span>
                    <span style={{ fontSize: "1.5rem" }}>
                      {ultimaMedida?.humidity ? "." + (ultimaMedida.humidity % 1).toFixed(1).substring(2) : ""}
                    </span>
                    <span style={{ fontSize: "1.5rem", color: "#a4c639" }}>{ultimaMedida.humidity ? " %" : ""}</span>
                  </h1>
                </Card.Body>
              </Card>
            </Col>

            <Col md={metricColSize}>
              <Card className="mb-0 h-100 shadow-sm dark-card" style={{ borderRadius: '12px', border: 'none', backgroundColor: '#1e2128' }}>
                <Card.Body className="p-2 text-center">
                  <div className="d-flex align-items-center justify-content-center mb-2">
                    <WiThermometer size={30} color="#ff7e5f" />
                    <h6 style={{ color: "#ff7e5f", marginBottom: 0, marginLeft: '5px' }}>TEMPERATURA</h6>
                  </div>
                  <h1 style={{ color: "#e0e0e0", marginBottom: 0, fontWeight: 700 }}>
                    <span style={{ fontSize: "2.0rem" }}>
                      {ultimaMedida?.temperature ? Math.floor(ultimaMedida.temperature) : "--"}
                    </span>
                    <span style={{ fontSize: "1.5rem" }}>
                      {ultimaMedida?.temperature && "." + (ultimaMedida.temperature % 1).toFixed(1).substring(2)}
                    </span>
                    <span style={{ fontSize: "1.5rem", color: "#ff7e5f" }}>{ultimaMedida.temperature ? " °C" : ""}</span>
                  </h1>
                </Card.Body>
              </Card>
            </Col>
            {showFardColumn && (
              <Col md={4}>
                <Card className="mb-0 h-100 shadow-sm dark-card" style={{ borderRadius: '12px', border: 'none', backgroundColor: '#1e2128' }}>
                  <Card.Body className="p-2 text-center">
                    <div className="d-flex align-items-center justify-content-center mb-2">
                      <TbPackage size={24} color="#5c9edc" />
                      <h6 style={{ color: "#5c9edc", marginBottom: 0, marginLeft: '5px' }}>
                        FARDOS/HORA
                      </h6>
                    </div>
                    <h1 style={{ color: "#e0e0e0", marginBottom: 0, fontWeight: 700 }}>
                      <>
                        <span style={{ fontSize: "2.0rem" }}>
                          {(fardosPorHora !== null && fardosPorHora !== undefined) ? fardosPorHora : "--"}
                        </span>
                        <span style={{ fontSize: "1.2rem", color: "#b0b0b0" }}> fardos/h</span>
                      </>
                    </h1>
                  </Card.Body>
                </Card>
              </Col>
            )}
          </Row>

          <div style={{ height: 250, position: 'relative', backgroundColor: '#1e2128', borderRadius: '12px', padding: '10px' }}>
            <Line
              data={chartData}
              plugins={[plugins]}
              options={chartOptions}
            />
          </div>
        </Card.Body>
      </Card>
    </Col >
  );
};

export default Grafico;
